import { readiness, type RoomCondition } from '@hk/ui'
import {
  type BoardRoom,
} from '@hk/core'

/* ============================================================================
   boardFilters.ts — the filter chips above the board, and their counts.

   THE COUNT AND THE FILTER ARE ONE THING. Each chip is defined by a single predicate;
   the count is the length of what that predicate returns and the filtered board is
   what that same predicate returns. They cannot be computed from different code
   because there is only one piece of code.

   A chip reading "Dirty 17" beside a filter that yields 18 rows is the fastest way to
   lose a supervisor's trust in a whole screen, and it is the defect that took longest
   to spot during the design build. It is structurally impossible here.
   ========================================================================= */

export type BoardFilterKey =
  | 'ALL' | 'READY' | 'DIRTY' | 'IN_PROGRESS' | 'AWAITING_INSPECTION'
  | 'NOT_IN_SERVICE' | 'OCCUPIED' | 'DEPARTING'

export const conditionOf = (room: BoardRoom): RoomCondition => ({
  housekeeping: room.housekeeping,
  occupancy: room.occupancy,
  service: room.service,
})

export interface BoardFilter {
  key: BoardFilterKey
  /** The single predicate. Both the count and the visible rooms come from this. */
  matches: (room: BoardRoom, requireInspection: boolean) => boolean
  /** The legend swatch, where the chip stands for a tile colour. */
  swatch?: string
}

export const BOARD_FILTERS: readonly BoardFilter[] = [
  { key: 'ALL', matches: () => true },

  {
    key: 'READY',
    // Derived, never a stored flag — and derived with the property's own inspection
    // policy, so a property that does not inspect counts CLEAN rooms as ready here
    // exactly as the tiles colour them.
    matches: (r, requireInspection) =>
      readiness(conditionOf(r), { requireInspection }).ready,
    swatch: 'var(--hk-hk-ready-border)',
  },
  {
    key: 'DIRTY',
    matches: (r) => r.housekeeping === 'DIRTY',
    swatch: 'var(--hk-hk-dirty-border)',
  },
  {
    key: 'IN_PROGRESS',
    matches: (r) => r.housekeeping === 'IN_PROGRESS',
    swatch: 'var(--hk-hk-prog-border)',
  },
  {
    key: 'AWAITING_INSPECTION',
    // Only meaningful where the property inspects. Where it does not, the predicate
    // still answers honestly (a CLEAN room is ready, not awaiting anything) rather
    // than the chip being hidden and the count going missing.
    matches: (r, requireInspection) =>
      requireInspection &&
      readiness(conditionOf(r), { requireInspection }).reason === 'AWAITING_INSPECTION',
    swatch: 'var(--hk-hk-clean-border)',
  },
  {
    key: 'NOT_IN_SERVICE',
    matches: (r) => r.service !== 'IN_SERVICE',
    swatch: 'var(--hk-sv-oos-border)',
  },
  { key: 'OCCUPIED', matches: (r) => r.occupancy === 'OCCUPIED' },
  { key: 'DEPARTING', matches: (r) => r.occupancy === 'DEPARTING' },
] as const

export function applyFilter(
  rooms: readonly BoardRoom[],
  key: BoardFilterKey,
  requireInspection: boolean,
): BoardRoom[] {
  const filter = BOARD_FILTERS.find((f) => f.key === key)
  if (!filter) return [...rooms]
  return rooms.filter((r) => filter.matches(r, requireInspection))
}

/** The counts shown on the chips. Same predicates, same array, same call. */
export function filterCounts(
  rooms: readonly BoardRoom[],
  requireInspection: boolean,
): Record<BoardFilterKey, number> {
  const counts = {} as Record<BoardFilterKey, number>
  for (const filter of BOARD_FILTERS) {
    counts[filter.key] = rooms.filter((r) => filter.matches(r, requireInspection)).length
  }
  return counts
}

/**
 * Floor grouping, in the order the server gave the floors.
 *
 * THE BUILDING TRAVELS WITH THE GROUP RATHER THAN NESTING IT. A hotel of four towers
 * reads down one tower at a time, and the server already orders the floors that way — so
 * the screen draws a flat list and starts a new heading when the building changes.
 *
 * Nesting would have been the obvious shape and is the wrong one here: a two-level
 * structure makes every empty tower a container to skip, and "skip the empty ones" is
 * exactly the rule the `.filter` below already applies one level down.
 */
export function groupByFloor(
  rooms: readonly BoardRoom[],
  floors: readonly { floorId: string; buildingId?: string }[],
): { floorId: string; buildingId: string; rooms: BoardRoom[] }[] {
  return floors
    .map((f) => ({
      floorId: f.floorId,
      buildingId: f.buildingId ?? '',
      rooms: rooms.filter((r) => r.floorId === f.floorId),
    }))
    .filter((g) => g.rooms.length > 0)
}
